import { Injectable, inject, computed, effect } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { injectQuery, queryOptions } from '@ngneat/query';
import { derivedAsync } from 'ngxtension/derived-async';
import { firstValueFrom } from 'rxjs';
import { VersionConfig } from '../models/version.model';

const STORAGE_KEY = 'app_version';

@Injectable({
  providedIn: 'root'
})
export class VersionBannerState {
  private readonly http = inject(HttpClient);
  private readonly query = injectQuery();

  private getVersionQueryOptions() {
    return queryOptions({
      queryKey: ['version-flag'] as const,
      queryFn: async () => {
        return await firstValueFrom(
          this.http.get<VersionConfig>('/assets/version.json')
        );
      },
      refetchInterval:  60 * 1000,
      staleTime: 0,
    });
  }

  readonly versionCheck = derivedAsync(() => {
    return this.query(this.getVersionQueryOptions()).result$;
  });

  readonly isLoading = computed(() => {
    const check = this.versionCheck();
    return check?.isLoading ?? false;
  });

  readonly error = computed(() => {
    const check = this.versionCheck();
    return check?.error ?? null;
  });

  readonly remoteVersion = computed(() => {
    const check = this.versionCheck();
    return check?.data?.version ?? null;
  });

  readonly shouldShowBanner = computed(() => {
    const remoteVersion = this.remoteVersion();
    
    if (!remoteVersion) return false;

    const localVersion = localStorage.getItem(STORAGE_KEY);
    return localVersion !== remoteVersion;
  });

  readonly bannerMessage = computed(() => {
    const check = this.versionCheck();
    return check?.data?.message ?? 'Una nueva versión está disponible. Por favor, recargue la página.';
  });

  constructor() {
    effect(() => {
      const remoteVersion = this.remoteVersion();
      if (remoteVersion && !localStorage.getItem(STORAGE_KEY)) {
        localStorage.setItem(STORAGE_KEY, remoteVersion);
      }
    });
  }

  updateLocalVersion(): void {
    const remoteVersion = this.remoteVersion();
    if (remoteVersion) {
      localStorage.setItem(STORAGE_KEY, remoteVersion);
    }
  }
}
